// src/admin/FormEditor/components/ImageUploader.jsx
// Handles image uploads for header/footer with multi-language support
// Features:
// - Supports both file upload and URL input
// - Converts uploaded images to base64
// - Drag and drop support
// - Clears previous image when new one is uploaded
// - Manages images per language code
// Props:
// - type: 'header' or 'footer' to determine image type

import { Image as ImageIcon, X } from 'lucide-react';
import { memo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateFormMetadata } from '../store/formEditorSlice';

const ImageUploader = memo(({ type }) => {
  const dispatch = useDispatch();
  const formMetadata = useSelector(state => state.formEditor.formMetadata);

  // Create refs for file inputs for each language
  const fileInputRefs = {
    en: useRef(),
    tc: useRef(),
    sc: useRef()
  };

  const handleImageUpload = (langcode, file) => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const updates = {
          [`${type}_base64`]: {
            ...formMetadata[`${type}_base64`],
            [langcode]: e.target.result
          },
          [`${type}_url`]: {
            ...formMetadata[`${type}_url`],
            [langcode]: ''  // Clear URL when uploading base64
          }

        }
        dispatch(updateFormMetadata({ updates }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleClearImage = (langcode) => {
    const updates = {
      [`${type}_base64`]: {
        ...formMetadata[`${type}_base64`],
        [langcode]: ''
      },
      [`${type}_url`]: {
        ...formMetadata[`${type}_url`],
        [langcode]: ''
      }
    };
    dispatch(updateFormMetadata({ updates }));
  };

  const handleUrlChange = (langcode, url) => {
    const updates = {
      [`${type}_url`]: {
        ...formMetadata[`${type}_url`],
        [langcode]: url
      },
      [`${type}_base64`]: {
        ...formMetadata[`${type}_base64`],
        [langcode]: ''  // Clear base64 when entering URL
      }
    }
    dispatch(updateFormMetadata({ updates }));
  };

  return (
    <div className="border-b grid grid-cols-1 p-2 gap-4">
      {formMetadata.supported_languages.map(langcode => (
        <div key={langcode} className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium text-gray-700">
              {langcode.toUpperCase()} {type.charAt(0).toUpperCase() + type.slice(1)}
            </div>
            <button
              onClick={() => handleClearImage(langcode)}
              className={`p-1 ${formMetadata[`${type}_base64`][langcode] || formMetadata[`${type}_url`][langcode]
                ? 'text-red-500 hover:text-red-600'
                : 'text-gray-300 cursor-not-allowed'
                }`}
              disabled={!formMetadata[`${type}_base64`][langcode] && !formMetadata[`${type}_url`][langcode]}
              title={`Remove ${type}`}
            >
              <X size={16} />
            </button>
          </div>

          <div className="space-y-2">
            <div
              className="border-2 border-dashed rounded-lg text-center cursor-pointer hover:border-blue-500 p-4"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                handleImageUpload(langcode, e.dataTransfer.files[0]);
              }}
              onClick={() => fileInputRefs[langcode].current.click()}
            >
              {(formMetadata[`${type}_base64`][langcode] || formMetadata[`${type}_url`][langcode]) ? (
                <img
                  src={formMetadata[`${type}_base64`][langcode] || formMetadata[`${type}_url`][langcode]}
                  alt={`${type} ${langcode}`}
                  className="max-h-40 mx-auto"
                />
              ) : (
                <div className="flex flex-col items-center text-gray-500">
                  <ImageIcon className="w-8 h-8" />
                  <p>Drag and drop or click to upload {type}</p>
                </div>
              )}
              <input
                type="file"
                ref={fileInputRefs[langcode]}
                className="hidden"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) handleImageUpload(langcode, file);
                }}
              />
            </div>

            <input
              type="text"
              value={formMetadata[`${type}_url`][langcode] || ''}
              onChange={(e) => handleUrlChange(langcode, e.target.value)}
              placeholder="Or enter image URL"
              className="w-full border rounded-md p-2"
            />
          </div>
        </div>
      ))}
    </div>
  );
});

export default ImageUploader;